import {
  addItemToCartAction,
  applyPromotionsAction,
  clearCartAction,
  fetchStoreOrderAction,
  fetchTimeSlotsAction,
  getEligiblePromotionsAction,
  prepareCheckoutAction,
  resetAppointmentAction,
  syncAppointmentTimeAction,
  syncDeliveryAddressTwoAction,
  syncDeliveryLocationAction,
  syncMethodAction,
  syncPickupLocationAction,
  togglePromotionAction,
  updateStoreOrderItemQuantityAction
} from './action'
import { createSlice } from '@reduxjs/toolkit'
import { appendSelectedItems } from 'src/utils/promotion'
import { HYDRATE } from 'next-redux-wrapper'

type PlainInfo<T> = {
  status: RequestState
  error?: string
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface IEligiblePromotions {
  promotions: SelectedPromotion[]
  promoCodePromotions: SelectedPromotion[]
  promoCodeEnabled: boolean
  ok: boolean
}

interface IAvailableTimeSlots {
  id: string
  name: string
  description: string
  stackableOption: number
  isPromoCodePromotion: boolean
  x: string
  y: string
}

interface OrderState {
  storeOrder: PlainInfo<Oms.V4.StoreOrder>
  eligiblePromotions: PlainInfo<IEligiblePromotions>
  selectedPromotions: PlainInfo<SelectedPromotion[]>
  availableTimeSlots: PlainInfo<IAvailableTimeSlots[]>
}

const initialState: OrderState = {
  storeOrder: {
    status: 'none',
    data: {} as Oms.V4.StoreOrder
  },
  eligiblePromotions: {
    status: 'none',
    data: {} as IEligiblePromotions
  },
  selectedPromotions: {
    status: 'none',
    data: [] as SelectedPromotion[]
  },
  availableTimeSlots: {
    status: 'none',
    data: [] as IAvailableTimeSlots[]
  }
}

export type OrderStateInReducer = {
  '@order': OrderState
}

export const orderSlice = createSlice({
  name: '@order',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(syncDeliveryAddressTwoAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(syncDeliveryAddressTwoAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        state.storeOrder.data.deliveryAddress = action.payload.data.address
        state.storeOrder.data.deliveryAddressTwo =
          action.payload.data.addressTwo
      })
      .addCase(syncDeliveryAddressTwoAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(prepareCheckoutAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(prepareCheckoutAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        state.storeOrder.data = action.payload?.payload
        if (action.payload?.payload?.ok && action.payload.payload) {
          state.storeOrder.data = action.payload.payload.storeOrder
        } else {
          state.storeOrder.status = 'rejected'
          // state.storeOrder.data = action.error.storeOrder
          // state.storeOrder.error = action.error.errors
        }
      })
      .addCase(prepareCheckoutAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(resetAppointmentAction, (state) => {
        state.storeOrder.data.collectionDate = null
        state.storeOrder.data.collectionTime = null
        state.storeOrder.data.deliveryAddress = null
        state.storeOrder.data.storeAddress = null
      })
      .addCase(togglePromotionAction, (state, action) => {
        const promotion = action.payload.promotion
        const idx = state.selectedPromotions.data.findIndex(
          (p) => p.id === promotion.id
        )
        if (idx < 0) {
          if (promotion.isPromoCodePromotion && promotion.stackableOption) {
            state.selectedPromotions.data =
              state.selectedPromotions.data.filter(
                (p) => p.isPromoCodePromotion
              )
          }
          state.selectedPromotions.data.push(promotion)
        } else {
          state.selectedPromotions.data.splice(idx, 1)
        }
      })
      .addCase(getEligiblePromotionsAction.pending, (state) => {
        state.eligiblePromotions.status = 'pending'
      })
      .addCase(getEligiblePromotionsAction.fulfilled, (state, action) => {
        state.eligiblePromotions.status = 'fulfilled'
        state.eligiblePromotions.data = action.payload.payload
        state.selectedPromotions.data = appendSelectedItems(
          state.eligiblePromotions.data.promotions,
          state.storeOrder.data.orderPromotions,
          state.storeOrder.data.orderItems
        ).concat(
          appendSelectedItems(
            state.eligiblePromotions.data.promoCodePromotions,
            state.storeOrder.data.orderPromotions,
            state.storeOrder.data.orderItems
          )
        )
      })
      .addCase(getEligiblePromotionsAction.rejected, (state, action) => {
        state.eligiblePromotions.status = 'rejected'
        state.eligiblePromotions.error = action.error.message
      })
      .addCase(addItemToCartAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(addItemToCartAction.fulfilled, (state, action) => {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('lastTimeTouchOrder', new Date().toString())
        }
        state.storeOrder.status = 'fulfilled'
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        }
      })
      .addCase(addItemToCartAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(applyPromotionsAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(applyPromotionsAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        } else {
          state.selectedPromotions.data = []
        }
      })
      .addCase(applyPromotionsAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(syncDeliveryLocationAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(syncDeliveryLocationAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        }
      })
      .addCase(syncDeliveryLocationAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(syncPickupLocationAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(syncPickupLocationAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        }
      })
      .addCase(syncPickupLocationAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(syncMethodAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(syncMethodAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        }
      })
      .addCase(syncMethodAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(clearCartAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(clearCartAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        }
      })
      .addCase(clearCartAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(fetchStoreOrderAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(fetchStoreOrderAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (action.payload?.payload?.ok) {
          state.storeOrder.data = action.payload.payload.storeOrder
        } else {
          state.storeOrder.status = 'rejected'
          state.storeOrder.error = action.payload?.payload?.error || null
        }
      })
      .addCase(fetchStoreOrderAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(fetchTimeSlotsAction.pending, (state) => {
        state.availableTimeSlots.status = 'pending'
      })
      .addCase(fetchTimeSlotsAction.fulfilled, (state, action) => {
        state.availableTimeSlots.status = 'fulfilled'
        state.availableTimeSlots.data = action.payload.payload.timeIntervals
      })
      .addCase(fetchTimeSlotsAction.rejected, (state, action) => {
        state.availableTimeSlots.status = 'rejected'
        state.availableTimeSlots.error = action.error.message
      })
      .addCase(syncAppointmentTimeAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(syncAppointmentTimeAction.fulfilled, (state, action) => {
        state.storeOrder.status = 'fulfilled'
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('lastTimeTouchOrder', new Date().toString())
        }
        if (action.payload.payload?.storeOrder) {
          state.storeOrder.data = action.payload.payload.storeOrder
        }
      })
      .addCase(syncAppointmentTimeAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(updateStoreOrderItemQuantityAction.pending, (state) => {
        state.storeOrder.status = 'pending'
      })
      .addCase(
        updateStoreOrderItemQuantityAction.fulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (action.payload?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.payload.storeOrder
          }
        }
      )
      .addCase(updateStoreOrderItemQuantityAction.rejected, (state, action) => {
        state.storeOrder.status = 'rejected'
        state.storeOrder.error = action.error.message || null
      })
      .addCase(HYDRATE, (state, action) => ({
        ...state,
        ...action['payload']['@order']
      }))
})
